import {
    AdminUserType,
    ClinicActivePlanType,
    ClinicCategoryAverageType,
    ClinicClinicalReportSummaryType,
    ClinicDailyPreviewType,
    ClinicEntityInfo,
    ClinicInsightDashboardType,
    ClinicMonthlyPreviewType,
    ClinicPatientType,
    ClinicPatientReportType,
    ClinicPreviewDayType,
    ClinicPreviewDisplayType,
    ClinicPreviewMonthType,
    ClinicPreviewScoreType,
    ClinicPreviewWeekType,
    ClinicProgressOverviewType,
    ClinicProgressPreviewType,
    ClinicWeeklyPreviewType,
} from "../../DTO";
import { convertAdminUser } from "../admin-user";

export const convertEntityInfo = (data: any): ClinicEntityInfo | null => {
    if (!data) {
        return null;
    }

    return {
        id: data?.id ?? null,
        name: data?.name ?? null,
        firstName: data?.first_name ?? data?.firstName ?? null,
        lastName: data?.last_name ?? data?.lastName ?? null,
        email: data?.email ?? null,
        phone: data?.phone ?? null,
        clinicNumber: data?.clinic_number ?? null,
        doctorName: data?.doctor_name ?? null,
        ...data,
    };
};

const convertPatientProfile = (data: any) => {
    if (!data) {
        return null;
    }

    return {
        id: data?.id ?? null,
        userId: data?.user_id ?? null,
        condition: data?.condition ?? null,
        therapy: data?.therapy ?? null,
    };
};

const convertPatientReport = (data: any): ClinicPatientReportType | null => {
    if (!data) {
        return null;
    }

    return {
        fileName: data?.file_name ?? data?.fileName ?? null,
        path: data?.path ?? null,
        url: data?.url ?? null,
        downloadUrl: data?.download_url ?? data?.downloadUrl ?? null,
    };
};

const convertClinicalReportSummary = (
    data: any,
): ClinicClinicalReportSummaryType | null => {
    if (!data) {
        return null;
    }

    return {
        age: data?.age ?? null,
        sex: data?.sex ?? null,
        conditionLabel: data?.condition_label ?? data?.conditionLabel ?? null,
        riskLabel: data?.risk_label ?? data?.riskLabel ?? null,
        reportingPeriod: data?.reporting_period ?? data?.reportingPeriod ?? null,
        monthsOnD2type:
            data?.months_on_d2type ?? data?.monthsOnD2type ?? null,
        clinicalSummary: Array.isArray(data?.clinical_summary)
            ? data.clinical_summary.map((item: any) => ({
                  label: item?.label ?? "",
                  value: item?.value ?? "",
              }))
            : [],
        aiInsights: Array.isArray(data?.ai_insights) ? data.ai_insights : [],
        recommendedActions: Array.isArray(data?.recommended_actions)
            ? data.recommended_actions
            : [],
    };
};

const convertUserInformation = (data: any) => {
    if (!Array.isArray(data)) {
        return [];
    }

    return data.map((item: any) => ({
        id: item?.id ?? null,
        userId: item?.user_id ?? null,
        planOrderId: item?.plan_order_id ?? null,
        height: item?.height ?? null,
        heightUnit: item?.height_unit ?? null,
        weight: item?.weight ?? null,
        weightUnit: item?.weight_unit ?? null,
        activityLevel: item?.activity_level ?? null,
        createdAt: item?.created_at ?? null,
        updatedAt: item?.updated_at ?? null,
    }));
};

const convertPlanOrder = (data: any) => {
    if (!data) {
        return null;
    }

    return {
        id: data?.id ?? null,
        planId: data?.plan_id ?? null,
        userId: data?.user_id ?? null,
        peerGroupId: data?.peer_group_id ?? null,
        period: data?.period ?? null,
        expiredAt: data?.expired_at ?? null,
        startedAt: data?.started_at ?? null,
        price: data?.price ?? null,
        status: data?.status ?? null,
        point: data?.point ?? null,
        createdAt: data?.created_at ?? null,
        updatedAt: data?.updated_at ?? null,
    };
};

const convertCreatedByAdmin = (
    data: any,
): AdminUserType | ClinicEntityInfo | null => {
    if (!data) {
        return null;
    }

    if (data?.created_at || data?.updated_at || data?.username) {
        return convertAdminUser(data);
    }

    return convertEntityInfo(data);
};

const convertPreviewDisplay = (data: any): ClinicPreviewDisplayType | null => {
    if (!data) {
        return null;
    }

    return {
        title: data?.title ?? null,
        light: data?.light ?? null,
        colorCode: data?.color_code ?? null,
        emoji: data?.emoji ?? null,
    };
};

const convertPreviewScore = (data: any): ClinicPreviewScoreType | null => {
    if (!data) {
        return null;
    }

    return {
        total: data?.total ?? null,
        max: data?.max ?? null,
        percentage: data?.percentage ?? null,
    };
};

const convertActivePlan = (data: any): ClinicActivePlanType | null => {
    if (!data) {
        return null;
    }

    return {
        planOrderId: data?.plan_order_id ?? null,
        startedAt: data?.started_at ?? null,
        expiredAt: data?.expired_at ?? null,
        currentWeekId: data?.current_week_id ?? null,
        currentDayId: data?.current_day_id ?? null,
    };
};

const convertDailyPreview = (data: any): ClinicDailyPreviewType | null => {
    if (!data) {
        return null;
    }

    return {
        id: data?.id ?? null,
        dayId: data?.day_id ?? null,
        display: convertPreviewDisplay(data?.display),
        score: convertPreviewScore(data?.score),
        summaryPreview: data?.summary?.preview ?? null,
        worstCategory: data?.worst_category ?? null,
        dailyInsight: data?.daily_insight ?? null,
        tomorrowFocus: data?.tomorrow_focus ?? null,
        generatedAt: data?.generated_at ?? null,
    };
};

const convertWeeklyPreview = (data: any): ClinicWeeklyPreviewType | null => {
    if (!data) {
        return null;
    }

    return {
        id: data?.id ?? null,
        weekId: data?.week_id ?? null,
        display: convertPreviewDisplay(data?.display),
        score: convertPreviewScore(data?.score),
        trendDirection: data?.trend?.direction ?? null,
        trendScoreChange: data?.trend?.score_change ?? null,
        worstCategory: data?.worst_category ?? null,
        weeklyInsight: data?.weekly_insight ?? null,
        generatedAt: data?.generated_at ?? null,
    };
};

const convertCategoryAverages = (
    data: any,
): Record<string, ClinicCategoryAverageType> | null => {
    if (!data || typeof data !== "object") {
        return null;
    }

    return Object.entries(data).reduce(
        (acc, [key, value]: [string, any]) => ({
            ...acc,
            [key]: {
                avgScore: value?.avg_score ?? null,
                light: value?.light ?? null,
            },
        }),
        {},
    );
};

const convertMonthlyPreview = (data: any): ClinicMonthlyPreviewType | null => {
    if (!data) {
        return null;
    }

    return {
        id: data?.id ?? null,
        monthKey: data?.month_key ?? data?.month?.key ?? null,
        monthStartDate: data?.month?.start_date ?? null,
        monthEndDate: data?.month?.end_date ?? null,
        display: convertPreviewDisplay(data?.display),
        score: convertPreviewScore(data?.score),
        trendDirection: data?.trend?.direction ?? null,
        trendScoreChange: data?.trend?.score_change ?? null,
        trendDescription: data?.trend?.description ?? null,
        daysWithDataCount:
            data?.days_with_data_count ??
            data?.traffic_light_distribution?.days_with_data ??
            null,
        greenDaysCount: data?.traffic_light_distribution?.green_days ?? null,
        amberDaysCount: data?.traffic_light_distribution?.amber_days ?? null,
        redDaysCount: data?.traffic_light_distribution?.red_days ?? null,
        worstCategory:
            data?.worst_category ?? data?.insights?.worst_category ?? null,
        monthlyInsight:
            data?.insights?.monthly_insight ??
            data?.monthly_insight_preview ??
            null,
        monthlyInsightPreview:
            data?.insights?.coach_summary_preview ??
            data?.monthly_insight_preview ??
            null,
        nutritionTotals: data?.nutrition?.totals ?? null,
        nutritionDailyAverages: data?.nutrition?.daily_averages ?? null,
        categoryAverages: convertCategoryAverages(data?.category_averages),
        generatedAt: data?.generated_at ?? null,
    };
};

const convertPreviewWeek = (data: any): ClinicPreviewWeekType => {
    return {
        id: data?.id ?? 0,
        title: data?.title ?? "Week",
        index: data?.index ?? 0,
        startDate: data?.startDate ?? data?.start_date ?? null,
        endDate: data?.endDate ?? data?.end_date ?? null,
        offset: data?.offset ?? null,
        isCurrentWeek: Boolean(data?.isCurrentWeek),
    };
};

const convertPreviewMonth = (data: any): ClinicPreviewMonthType => {
    return {
        month: data?.month ?? "",
        monthDisplay: data?.month_display ?? data?.month ?? "",
        startDate: data?.start_date ?? null,
        endDate: data?.end_date ?? null,
        isCurrentMonth: Boolean(data?.is_current_month),
        hasData: Boolean(data?.has_data),
        daysWithData: data?.days_with_data ?? null,
    };
};

const convertPreviewDay = (data: any): ClinicPreviewDayType => {
    return {
        id: data?.id ?? 0,
        name: data?.name ?? "",
        date: data?.date ?? null,
        percentage: data?.percentage ?? null,
        isLock: Boolean(data?.isLock),
        isCurrentDay: Boolean(data?.isCurrentDay),
    };
};

const convertProgressOverview = (data: any): ClinicProgressOverviewType | null => {
    if (!data) {
        return null;
    }

    return {
        hasActivePlan: Boolean(data?.has_active_plan),
        activePlan: convertActivePlan(data?.active_plan),
        latestDaily: convertDailyPreview(data?.latest_daily),
        latestWeekly: convertWeeklyPreview(data?.latest_weekly),
        latestMonthly: convertMonthlyPreview(data?.latest_monthly),
    };
};

const convertProgressPreview = (data: any): ClinicProgressPreviewType | null => {
    if (!data) {
        return null;
    }

    return {
        activePlan: convertActivePlan(data?.active_plan),
        months: Array.isArray(data?.months)
            ? data.months.map(convertPreviewMonth)
            : [],
        weeks: Array.isArray(data?.weeks) ? data.weeks.map(convertPreviewWeek) : [],
        currentWeekDays: Array.isArray(data?.current_week_days)
            ? data.current_week_days.map(convertPreviewDay)
            : [],
        latestDaily: convertDailyPreview(data?.latest_daily),
        latestWeekly: convertWeeklyPreview(data?.latest_weekly),
        latestMonthly: convertMonthlyPreview(data?.latest_monthly),
    };
};

export const convertClinicPatient = (data: any): ClinicPatientType => {
    const patientProfile = convertPatientProfile(data?.patient_profile);

    return {
        id: data?.id ?? 0,
        userId: data?.user_id ?? null,
        fullName: data?.name ?? null,
        firstName: data?.first_name ?? "",
        lastName: data?.last_name ?? "",
        email: data?.email ?? null,
        phone: data?.phone ?? null,
        identityCode: data?.identity_code ?? null,
        insuranceMemberId: data?.insurance_member_id ?? null,
        status: data?.status ?? null,
        isJoined: Boolean(data?.is_joined ?? data?.user_id),
        progressStatus: data?.progress_status ?? null,
        clinicId: data?.clinic_id ?? null,
        createdByAdminId: data?.created_by_admin_id ?? null,
        condition: data?.condition ?? patientProfile?.condition ?? null,
        therapy: data?.therapy ?? patientProfile?.therapy ?? null,
        diseaseType: data?.disease_type ?? null,
        registerState: data?.register_state ?? null,
        onboardingStatus: data?.onboarding_status ?? null,
        signupSource: data?.signup_source ?? null,
        createdAt: data?.created_at ?? null,
        updatedAt: data?.updated_at ?? null,
        birthday: data?.birthday ?? null,
        city: data?.city ?? null,
        state: data?.state ?? null,
        postalCode: data?.postal_code ?? null,
        unit: data?.unit ?? null,
        patientProfile,
        report: convertPatientReport(data?.report),
        clinicalReportSummary: convertClinicalReportSummary(
            data?.clinical_report_summary,
        ),
        userInformation: convertUserInformation(data?.user_information),
        planOrder: convertPlanOrder(data?.plan_order),
        clinic: convertEntityInfo(data?.clinic),
        createdByAdmin: convertCreatedByAdmin(data?.created_by_admin),
        linkedUser: convertEntityInfo(data?.user),
        progressOverview: convertProgressOverview(data?.progress_overview),
        progressPreview: convertProgressPreview(data?.progress_preview),
        latestDailySummary: data?.latest_daily_summary ?? null,
        latestWeeklySummary: data?.latest_weekly_summary ?? null,
        latestMonthlySummary:
            convertMonthlyPreview(data?.latest_monthly_summary) ??
            data?.latest_monthly_summary ??
            null,
        raw: data,
    };
};

export const convertClinicPatientList = (data: any): ClinicPatientType[] => {
    if (Array.isArray(data)) {
        return data.map((item) => convertClinicPatient(item));
    }

    if (Array.isArray(data?.data)) {
        return data.data.map((item: any) => convertClinicPatient(item));
    }

    if (Array.isArray(data?.patient_profiles)) {
        return data.patient_profiles.map((item: any) => convertClinicPatient(item));
    }

    return [];
};

export const extractClinicPatient = (data: any): ClinicPatientType => {
    const patientData =
        data?.data?.id !== undefined
            ? data.data
            : data?.id !== undefined
              ? data
              : data?.data?.patient_profile
                ? {
                      ...data.data,
                      patient_profile: data.data.patient_profile,
                  }
                : data?.patient_profile
                  ? {
                        ...data,
                        patient_profile: data.patient_profile,
                    }
                  : data;

    return convertClinicPatient(patientData);
};

export const convertClinicInsights = (data: any): ClinicInsightDashboardType => {
    const summary = data?.summary ?? {};

    return {
        clinic: convertEntityInfo(data?.clinic),
        reportingPeriod: data?.reporting_period ?? null,
        summary: {
            totalPatients: summary?.total_patients ?? 0,
            linkedPatients: summary?.linked_patients ?? 0,
            highRisk: summary?.high_risk ?? 0,
            improving: summary?.improving ?? 0,
            newReferrals: summary?.new_referrals ?? 0,
        },
        cards: Array.isArray(data?.cards)
            ? data.cards.map((card: any) => ({
                  key: card?.key ?? "",
                  title: card?.title ?? "",
                  items: Array.isArray(card?.items) ? card.items : [],
              }))
            : [],
        raw: data,
    };
};
